import React, { useState, useEffect } from 'react';
import { Eye, Tag } from 'lucide-react';
import { MarkerInfo, MarkerRecord } from '../../services/api';
import { useResolvedMediaUrl } from '../../hooks/useResolvedMediaUrl';
import MarkerEditor from '../MarkerEditor';
import { PreviewData } from './MarkedTile';

interface MarkedFieldTileProps {
  record: MarkerRecord;
  previewData?: PreviewData;
  isHovered: boolean;
  onHover: (hovered: boolean) => void;
  onClick: () => void;
  onRemove: () => void;
  onMarkerChange: (next: MarkerRecord | null) => void;
}

const toMarkerInfo = (record: MarkerRecord): MarkerInfo => ({
  marker_id: record.marker_id,
  color: record.color ?? null,
  note: record.note ?? null,
  updated_at: record.updated_at ?? null,
});

const MarkedFieldTile: React.FC<MarkedFieldTileProps> = ({
  record,
  previewData,
  isHovered,
  onHover,
  onClick,
  onRemove,
  onMarkerChange,
}) => {
  const [localMarker, setLocalMarker] = useState(toMarkerInfo(record));

  // Prefer GIF when hovered, thumbnail otherwise
  const previewSource = isHovered && previewData?.gifUrl
    ? previewData.gifUrl
    : previewData?.thumbnailUrl;
  const durablePreviewSource = isHovered && previewData?.gifUrl
    ? previewData.gifGcsUri
    : previewData?.thumbnailGcsUri;
  const {
    resolvedUrl: previewUrl,
    isLoading: isPreviewLoading,
    refreshAfterError,
  } = useResolvedMediaUrl(previewSource, {
    durableSource: durablePreviewSource,
    videoId: record.video_id,
    segmentId: record.segment_id,
  });

  useEffect(() => {
    setLocalMarker({
      marker_id: record.marker_id,
      color: record.color ?? null,
      note: record.note ?? null,
      updated_at: record.updated_at ?? null,
    });
  }, [record.marker_id, record.color, record.note, record.updated_at]);

  const notePreview = (record.note || '').trim();
  const fieldKey = record.field_key || 'field';
  const displayFieldKey = fieldKey.length > 12 ? `${fieldKey.slice(0, 12)}...` : fieldKey;

  return (
    <div
      className="marked-tile"
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      {/* Marker Editor */}
      <div className="marked-tile-marker" onClick={(e) => e.stopPropagation()}>
        <MarkerEditor
          marker={localMarker}
          target={{
            entity_type: 'metadata_field',
            index_id: record.index_id || undefined,
            run_id: record.run_id || undefined,
            video_id: record.video_id || undefined,
            segment_id: record.segment_id || undefined,
            field_key: record.field_key || undefined,
            segment_start_time: record.segment_start_time ?? undefined,
            segment_end_time: record.segment_end_time ?? undefined,
          }}
          onMarkerChange={(next) => {
            setLocalMarker(next);
          }}
          onRecordChange={onMarkerChange}
        />
      </div>

      {/* Field Badge */}
      <div className="entity-badge field">
        <Tag size={10} />
        {displayFieldKey}
      </div>

      {/* Preview */}
      <div className="marked-tile-preview">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Field preview"
            className="marked-tile-image"
            onError={() => refreshAfterError(previewUrl)}
          />
        ) : (
          <div className={`marked-tile-placeholder ${isPreviewLoading ? 'loading' : ''}`}>
            {isPreviewLoading ? (
              <>
                <div className="loading-spinner-small" />
                <span>Loading</span>
              </>
            ) : (
              <>
                <Eye size={32} />
                <span>No Preview</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Info Overlay */}
      <div className="marked-tile-info">
        <div className="marked-tile-title">Field: {fieldKey}</div>
        {notePreview && <div className="marked-tile-note">{notePreview}</div>}
        {record.segment_id && (
          <div className="marked-tile-meta">Segment: {record.segment_id.slice(0, 8)}...</div>
        )}
      </div>
    </div>
  );
};

export default MarkedFieldTile;
